// Copyright (c) 2025, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { APIService } from "../../utils/apiService";
import { AppConfig } from "../../config/config";
import { State } from "../../types/types";

interface EmployeesState {
  state: State;
  stateMessage: string | null;
  errorMessage: string | null;
  employees: Employee[] | null;
  selectedEmail: string | null;
  lastFetched: number | null;
}

const initialState: EmployeesState = {
  state: State.idle,
  stateMessage: "",
  errorMessage: "",
  employees: null,
  selectedEmail: null,
  lastFetched: null,
};

export interface Employee {
  firstName: string;
  lastName: string;
  workEmail: string;
  employeeThumbnail: string | null;
}

export const fetchEmployees = createAsyncThunk(
  "employees/fetchEmployees",
  async () => {
    return new Promise<{ employees: Employee[] }>((resolve, reject) => {
      APIService.getInstance()
        .get(`${AppConfig.serviceUrls.getEmpInfo}`)
        .then((response) => {
          resolve({
            employees: response.data,
          });
        })
        .catch((error: Error) => {
          reject(error);
        });
    });
  }
);

// Employees Slice
const employeesSlice = createSlice({
  name: "employees",
  initialState,
  reducers: {
    setSelectedEmail: (state, action: PayloadAction<string | null>) => {
      state.selectedEmail = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEmployees.pending, (state) => {
        state.state = State.loading;
        state.stateMessage = "Fetching emails...";
      })
      .addCase(fetchEmployees.fulfilled, (state, action) => {
        state.employees = action.payload.employees;
        state.state = State.success;
        state.stateMessage = "Successfully fetched!";
        state.lastFetched = Date.now();
      })
      .addCase(fetchEmployees.rejected, (state, action) => {
        state.state = State.failed;
        state.stateMessage = "Failed to fetch!";
        state.errorMessage = action.payload as string;
      });
  },
});
export const { setSelectedEmail } = employeesSlice.actions;
export default employeesSlice.reducer;
